'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Spinner } from '@common/components';
import { SvgChevronright } from '@common/icon';
import { cn } from '@core/utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { UnauthInfiniteQueryOptions } from '@/api';
import { ReviewDetailSessionPortal } from '@/app/donation/_components/ReviewDetailSessionPortal';
import { Image } from '@/components/client';
import { SessionPortal } from '@/components/organism/SessionPortal/SessionPortal';
import { SELECTOR } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { openResponsiveDialog } from '@/stores/ResponsiveDialogStore';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';
import { PhotoReviewsModalContent } from '../gifts/[productNumber]/_components/PhotoReviewsModalContent';
const INITIAL_PAGE = 1;
const INITIAL_SIZE = 10;
interface PhotoReviewSwiperProps {
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  productNumber?: string;
  institutionId?: number;
  className?: string;
}
export const PhotoReviewSwiper = t0 => {
  const $ = _c(64);
  const {
    photoReviewList,
    photoReviewCount,
    productNumber,
    institutionId,
    className
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const [isOpen, setIsOpen] = useState(false);
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("detailReviewId");
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const detailReviewIdParam = t1;
  const detailReviewId = detailReviewIdParam ? Number(detailReviewIdParam) : undefined;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      align: "start",
      dragFree: true,
      containScroll: "trimSnaps"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [emblaRef] = useEmblaCarousel(t2);
  let t3;
  if ($[3] !== institutionId || $[4] !== photoReviewCount || $[5] !== photoReviewList || $[6] !== productNumber) {
    t3 = reviewId => {
      openResponsiveDialog({
        component: PhotoReviewsModalContent,
        modalHeader: "\uD3EC\uD1A0\uD6C4\uAE30",
        className: {
          modal: "max-w-[900px]"
        },
        props: {
          productNumber,
          institutionId,
          photoReviewList,
          photoReviewCount,
          initialReviewId: reviewId
        },
        drawerNoPadding: true
      });
    };
    $[3] = institutionId;
    $[4] = photoReviewCount;
    $[5] = photoReviewList;
    $[6] = productNumber;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleDetailReviewId = t3;
  let t4;
  if ($[8] !== institutionId || $[9] !== productNumber) {
    t4 = filledInfiniteQueryOptions(UnauthInfiniteQueryOptions["get:/wello/v2/allows/home/gift-review/photo"]({
      query: {
        productNumber,
        institutionId,
        page: INITIAL_PAGE,
        size: INITIAL_SIZE
      }
    }));
    $[8] = institutionId;
    $[9] = productNumber;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = !!productNumber || !!institutionId;
  let t6;
  if ($[11] !== t4 || $[12] !== t5) {
    t6 = {
      ...t4,
      enabled: t5
    };
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useInfiniteQuery(t6);
  const infinitePhotoReviewList = data?.pages.flatMap(_temp) ?? [];
  const {
    ref,
    inView
  } = useInView();
  let t7;
  if ($[14] !== fetchNextPage || $[15] !== inView || $[16] !== isFetching) {
    t7 = () => {
      if (!isFetching && inView) {
        fetchNextPage();
      }
    };
    $[14] = fetchNextPage;
    $[15] = inView;
    $[16] = isFetching;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== fetchNextPage || $[19] !== hasNextPage || $[20] !== inView || $[21] !== isFetching) {
    t8 = [inView, isFetching, fetchNextPage, hasNextPage];
    $[18] = fetchNextPage;
    $[19] = hasNextPage;
    $[20] = inView;
    $[21] = isFetching;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  useEffect(t7, t8);
  const isEmpty = photoReviewCount < 1;
  if (isEmpty) {
    return null;
  }
  let t9;
  if ($[23] !== className) {
    t9 = cn("flex flex-col pb-24", className);
    $[23] = className;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== photoReviewCount) {
    t10 = photoReviewCount.toLocaleString();
    $[25] = photoReviewCount;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10) {
    t11 = <h2 className="font-700 text-Gray-900 typo-body2">포토후기 {t10}</h2>;
    $[27] = t10;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== institutionId || $[30] !== isDesktopViewport || $[31] !== photoReviewCount || $[32] !== photoReviewList || $[33] !== productNumber) {
    t12 = () => {
      if (isDesktopViewport) {
        openResponsiveDialog({
          component: PhotoReviewsModalContent,
          modalHeader: "\uD3EC\uD1A0\uD6C4\uAE30 \uC804\uCCB4\uBCF4\uAE30",
          className: {
            modal: "max-w-[900px]"
          },
          props: {
            institutionId,
            productNumber,
            photoReviewList,
            photoReviewCount
          }
        });
      } else {
        setIsOpen(true);
      }
    };
    $[29] = institutionId;
    $[30] = isDesktopViewport;
    $[31] = photoReviewCount;
    $[32] = photoReviewList;
    $[33] = productNumber;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  let t13;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <SvgChevronright className="size-12" />;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== isEmpty || $[37] !== t12) {
    t14 = <Button theme="sub-text" className="flex size-fit items-center gap-4 px-4 py-10 font-400 text-Gray-900 typo-body4" disabled={isEmpty} onClick={t12}>더보기{t13}</Button>;
    $[36] = isEmpty;
    $[37] = t12;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t11 || $[40] !== t14) {
    t15 = <div className="flex items-center justify-between py-12">{t11}{t14}</div>;
    $[39] = t11;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== handleDetailReviewId || $[43] !== photoReviewList) {
    let t17;
    if ($[45] !== handleDetailReviewId) {
      t17 = t18 => {
        const {
          fileUrl,
          reviewId: reviewId_0
        } = t18;
        return <div key={reviewId_0} className="w-fit flex-[0_0_auto]"><button type="button" className="relative size-104 overflow-hidden rounded-small border border-Gray-black/5 transition-opacity duration-200 hover:opacity-80" onClick={() => handleDetailReviewId(reviewId_0)}><Image src={fileUrl} alt={"\uD3EC\uD1A0\uD6C4\uAE30 \uC774\uBBF8\uC9C0"} width={850} height={850} className="size-full object-cover object-center" /></button></div>;
      };
      $[45] = handleDetailReviewId;
      $[46] = t17;
    } else {
      t17 = $[46];
    }
    t16 = photoReviewList.map(t17);
    $[42] = handleDetailReviewId;
    $[43] = photoReviewList;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[47] !== t16) {
    t17 = <div className="flex gap-8">{t16}</div>;
    $[47] = t16;
    $[48] = t17;
  } else {
    t17 = $[48];
  }
  let t18;
  if ($[49] !== emblaRef || $[50] !== t17) {
    t18 = <div className="w-full overflow-hidden" ref={emblaRef}>{t17}</div>;
    $[49] = emblaRef;
    $[50] = t17;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  const t19 = !isDesktopViewport && isOpen ? <SessionPortal renderTo={SELECTOR.MAIN_WRAPPER} wrapperClassName="z-10" onClose={() => setIsOpen(false)}><div className="flex flex-col"><ul className="grid grid-cols-3 gap-12 px-20 pt-12">{infinitePhotoReviewList.map(t20 => {
          const {
            fileUrl: file_url,
            reviewId: review_id
          } = t20;
          return <li key={review_id}><button type="button" className="relative aspect-square overflow-hidden rounded-small border border-Gray-black/5 transition-opacity duration-200 hover:opacity-80" onClick={() => handleDetailReviewId(review_id)}><Image src={file_url} alt={"\uD3EC\uD1A0\uD6C4\uAE30 \uC774\uBBF8\uC9C0"} width={300} height={300} className="size-full object-cover object-center" /></button></li>;
        })}</ul>{hasNextPage ? <div className="my-30 w-full flex-center" ref={ref}><Spinner /></div> : null}</div></SessionPortal> : <></>;
  const t21 = !isDesktopViewport && !!detailReviewId;
  let t22;
  if ($[52] !== handleDetailReviewId) {
    t22 = () => handleDetailReviewId(undefined);
    $[52] = handleDetailReviewId;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  let t23;
  if ($[54] !== detailReviewId || $[55] !== t21 || $[56] !== t22) {
    t23 = <ReviewDetailSessionPortal open={t21} onClose={t22} reviewId={detailReviewId} />;
    $[54] = detailReviewId;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== t15 || $[59] !== t18 || $[60] !== t19 || $[61] !== t23 || $[62] !== t9) {
    t24 = <section className={t9}>{t15}{t18}{t19}{t23}</section>;
    $[58] = t15;
    $[59] = t18;
    $[60] = t19;
    $[61] = t23;
    $[62] = t9;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
};
function _temp(page) {
  return page.context?.contents ?? [];
}